import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useGuestSessionLifecycle } from 'pages/Main/QRPages/hooks/useGuestSessionLifecycle';
import { isGuestSessionReady, prepareGuestSession } from 'utils/guestSession';

/**
 * GuestSessionGate
 *
 * Route-level wrapper that scopes the guest-user session lifecycle to the
 * QR-facing routes (`/forms-qr/*` and `/qr/*`). Rest of the app is
 * untouched.
 *
 * Responsibilities:
 *
 * 1. Session-entry gate.
 *    On its very first render in a given tab, runs
 *    `prepareGuestSession()`. That helper inspects a `sessionStorage` flag
 *    and (if the flag is missing) broadcasts a presence ping to decide
 *    whether this is:
 *      - an already-active tab (reload / internal navigation) — proceed
 *        immediately,
 *      - a new tab opened from an existing sibling session (e.g. the
 *        QuickAccess "open form in new tab" flow for guests) — join the
 *        existing session and keep the tokens,
 *      - or a genuinely fresh browser session (including mobile process
 *        kill followed by relaunch, where `pagehide`-based cleanup can
 *        never run) — wipe any stale guest tokens from `localStorage`
 *        before `useAuth` reads them.
 *    Children are not rendered until that decision has been made, so
 *    `NonUserRoute`'s `useAuth` call sees a consistent `localStorage`
 *    state.
 *
 * 2. Sibling-presence responder.
 *    `useGuestSessionLifecycle()` keeps a `BroadcastChannel` listener
 *    open for as long as this tab is mounted, so future fresh tabs can
 *    discover us and skip cleanup when they mount.
 *
 * For performance, the initial `useState` seed uses the synchronous
 * `isGuestSessionReady()` helper: already-gated tabs (reload, internal
 * navigation within the QR subtree) render the outlet in the first pass
 * with no flash. Only a truly fresh mount renders null for the short BC
 * timeout window.
 */
export const GuestSessionGate = () => {
  const [isReady, setIsReady] = useState<boolean>(() => isGuestSessionReady());

  useEffect(() => {
    if (isReady) return undefined;
    let cancelled = false;
    prepareGuestSession().then(() => {
      if (!cancelled) setIsReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isReady]);

  useGuestSessionLifecycle();

  if (!isReady) return null;
  return <Outlet />;
};
